import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  CircularProgress,
  Alert,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

const AccountTransactionAdd = ({ open, onClose, token }) => {
  const { t } = useTranslation();
  const { handleAdd, loading, resetForm } = useAdd("shop/account-transactions", token);

  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "account-transaction-add"],
    "shop/accounts/?page_size=all",
    token
  );

  const accounts = Array.isArray(accountsData)
    ? accountsData
    : accountsData?.results || [];

  const [accountId, setAccountId] = useState("");
  const [transactionCurrencyId, setTransactionCurrencyId] = useState("");
  const [transactionType, setTransactionType] = useState("credit");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [transactionDate, setTransactionDate] = useState(
    new Date().toISOString().slice(0, 10)
  );

  const selectedAccount = useMemo(
    () => accounts.find((a) => String(a.id) === String(accountId)),
    [accounts, accountId]
  );
  const selectedShopId = selectedAccount?.shop || "";
  const accountCurrencyId = selectedAccount?.currency || "";

  const { data: currenciesData, isLoading: currencyLoading } = useFetchObjects(
    ["currencies", "account-transaction-add", selectedShopId],
    selectedShopId ? `shop/currencies/?page_size=all&shop=${selectedShopId}` : "",
    token
  );
  const currencies = Array.isArray(currenciesData)
    ? currenciesData
    : currenciesData?.results || [];

  const { data: directRateData } = useFetchObjects(
    ["account-transaction-rate-direct", selectedShopId, transactionCurrencyId, accountCurrencyId],
    selectedShopId && transactionCurrencyId && accountCurrencyId
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShopId}&from_currency=${transactionCurrencyId}&to_currency=${accountCurrencyId}`
      : "",
    token
  );

  const { data: reverseRateData } = useFetchObjects(
    ["account-transaction-rate-reverse", selectedShopId, transactionCurrencyId, accountCurrencyId],
    selectedShopId && transactionCurrencyId && accountCurrencyId
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShopId}&from_currency=${accountCurrencyId}&to_currency=${transactionCurrencyId}`
      : "",
    token
  );

  useEffect(() => {
    if (selectedAccount) {
      setTransactionCurrencyId(String(selectedAccount.currency));
    } else {
      setTransactionCurrencyId("");
    }
  }, [selectedAccount]);

  const directRate = directRateData?.results?.[0]?.rate;
  const reverseRate = reverseRateData?.results?.[0]?.rate;
  const exchangeRatePreview = useMemo(() => {
    if (!transactionCurrencyId || !accountCurrencyId) return "";
    if (String(transactionCurrencyId) === String(accountCurrencyId)) return "1";
    if (directRate) return String(directRate);
    if (reverseRate && Number(reverseRate) > 0) {
      return String(1 / Number(reverseRate));
    }
    return "";
  }, [transactionCurrencyId, accountCurrencyId, directRate, reverseRate]);

  const convertedAmountPreview = useMemo(() => {
    const nAmount = Number(amount);
    const nRate = Number(exchangeRatePreview);
    if (!Number.isFinite(nAmount) || !Number.isFinite(nRate) || nAmount <= 0 || nRate <= 0) {
      return "";
    }
    return (nAmount * nRate).toFixed(2);
  }, [amount, exchangeRatePreview]);

  const handleSubmit = () => {
    handleAdd({
      account: accountId,
      transaction_currency: transactionCurrencyId,
      transaction_type: transactionType,
      amount,
      description: description.trim() || null,
      transaction_date: transactionDate,
    });
  };

  useEffect(() => {
    if (resetForm) {
      setAccountId("");
      setTransactionCurrencyId("");
      setTransactionType("credit");
      setAmount("");
      setDescription("");
      setTransactionDate(new Date().toISOString().slice(0, 10));
      onClose();
    }
  }, [resetForm, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("accountTransaction.addTitle")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            select
            margin="dense"
            label={t("accountTransaction.account")}
            fullWidth
            required
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            {accountLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              accounts.map((account) => (
                <MenuItem key={account.id} value={String(account.id)}>
                  {account.name} ({account.currency_title || "-"})
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("accountTransaction.accountCurrency")}
            fullWidth
            value={selectedAccount?.currency_title || ""}
            disabled
          />

          <TextField
            select
            margin="dense"
            label={t("accountTransaction.transactionCurrency")}
            fullWidth
            required
            disabled={!selectedShopId || currencyLoading}
            value={transactionCurrencyId}
            onChange={(e) => setTransactionCurrencyId(e.target.value)}
          >
            {currencyLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              currencies.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.title}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            select
            margin="dense"
            label={t("accountTransaction.type")}
            fullWidth
            required
            value={transactionType}
            onChange={(e) => setTransactionType(e.target.value)}
          >
            <MenuItem value="credit">{t("accountTransaction.credit")}</MenuItem>
            <MenuItem value="debit">{t("accountTransaction.debit")}</MenuItem>
          </TextField>

          <TextField
            margin="dense"
            label={t("accountTransaction.amount")}
            type="number"
            inputProps={{ step: "0.01", min: "0.01" }}
            fullWidth
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />

          <TextField
            margin="dense"
            label={t("accountTransaction.exchangeRate")}
            fullWidth
            value={exchangeRatePreview}
            disabled
          />

          <TextField
            margin="dense"
            label={t("accountTransaction.convertedAmount")}
            fullWidth
            value={convertedAmountPreview}
            disabled
          />

          {!exchangeRatePreview && accountId && transactionCurrencyId && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {t("accountTransaction.missingRate")}
            </Alert>
          )}

          <TextField
            margin="dense"
            label={t("accountTransaction.transactionDate")}
            type="date"
            fullWidth
            required
            value={transactionDate}
            onChange={(e) => setTransactionDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            margin="dense"
            label={t("accountTransaction.description")}
            fullWidth
            multiline
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <TextField
            margin="dense"
            label={t("accountTransaction.accountBalance", "Account balance")}
            fullWidth
            value={
              selectedAccount
                ? `${selectedAccount.balance ?? "-"} ${selectedAccount.currency_title || ""}`.trim()
                : ""
            }
            disabled
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("accountTransaction.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !accountId ||
            !transactionCurrencyId ||
            !transactionType ||
            !amount ||
            Number(amount) <= 0 ||
            !transactionDate ||
            !exchangeRatePreview
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : t("accountTransaction.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountTransactionAdd;
